/**
 * @file DocumentItemsSection.tsx
 * @description Позиции документа (phase19.31, часть 3): название,
 * количество, цена, сумма и итог внизу.
 *
 * Позиции — с сервера (`items` карточки, контракт §19): у выпущенного — из
 * снимка печати, у черновика — из заказа, как их напечатает выпуск. От 1024 —
 * общая таблица позиций (`DocumentItemsTable`, без колонки фото), уже —
 * строки «название / 2 × 450,00 ₽ … 900,00 ₽». У документа без заказа
 * позиций нет — так и пишем.
 */

import { documentItems, formatDocMoney, formatQuantity, type DocumentItemView } from "./document-view";
import { DocumentItemsTable, type DocumentTableColumn } from "../../ui/DocumentItemsTable";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { SectionCard } from "../../ui/SectionCard";
import type { DocumentDto } from "../../../lib/api-crm";
import { cn } from "@/lib/utils";

interface Props {
  doc: DocumentDto;
}

/** Колонки таблицы позиций — данными. */
function itemColumns(currency: string): DocumentTableColumn<DocumentItemView>[] {
  return [
    { key: "qty", label: "Количество", width: "112px", align: "right", render: (i) => <span className="tabular-nums">{formatQuantity(i.quantity)}</span> },
    { key: "price", label: "Цена", width: "136px", align: "right", render: (i) => <span className="tabular-nums whitespace-nowrap">{formatDocMoney(i.price, currency)}</span> },
    { key: "total", label: "Сумма", width: "148px", align: "right", render: (i) => <span className="tabular-nums whitespace-nowrap font-medium">{formatDocMoney(i.total, currency)}</span> },
  ];
}

/** Почему позиций нет — словами по состоянию документа. */
function emptyText(doc: DocumentDto): string {
  if (!doc.orderId) return "У документа нет заказа-основания — позиции в нём не печатаются";
  return doc.status === "DRAFT" ? "В заказе нет позиций для документа" : "В документе нет позиций";
}

export function DocumentItemsSection({ doc }: Props) {
  const items = documentItems(doc);
  const title = items.length > 0 ? `Позиции · ${items.length}` : "Позиции";
  // Черновик: позиции — как их напечатает выпуск, пока не окончательные.
  const draftNote = doc.status === "DRAFT" && items.length > 0;

  return (
    <SectionCard title={title}>
      {draftNote ? <p className="type-caption text-on-surface-variant">Позиции заказа — так они попадут в документ при выпуске</p> : null}
      {items.length === 0 ? (
        <p className="type-body text-on-surface-variant">{emptyText(doc)}</p>
      ) : (
        <>
          <DocumentItemsTable
            items={items}
            rowKey={(i) => String(i.index)}
            columns={itemColumns(doc.currency)}
            media={() => null}
            title={(i) => i.name}
            hideMedia
            minWidth="0px"
          />
          <ul aria-label="Позиции документа" className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)] lg:hidden")}>
            {items.map((i) => (
              <li key={i.index} className="flex min-h-14 items-center justify-between gap-3 px-3 py-2">
                <span className="flex min-w-0 flex-col">
                  {/* Две строки названия: на 360 в одну влезало «Сила жизни, трав…». */}
                  <span className="type-body line-clamp-2 break-words font-medium text-on-surface">{i.name}</span>
                  <span className="type-caption tabular-nums text-on-surface-variant">
                    {formatQuantity(i.quantity)} × {formatDocMoney(i.price, doc.currency)}
                  </span>
                </span>
                <span className="type-body shrink-0 font-medium tabular-nums">{formatDocMoney(i.total, doc.currency)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <DocumentTotals doc={doc} />
    </SectionCard>
  );
}

/** Итог документа: «Итого 1 175,00 ₽», под ним НДС, если он есть. */
function DocumentTotals({ doc }: Props) {
  if (doc.totalGross === null) return null;
  // НДС: сумма — «в том числе НДС», ноль — «Без НДС», неизвестно — строки нет.
  const vat =
    doc.totalVat === null
      ? null
      : Number(doc.totalVat) > 0
        ? { label: "в том числе НДС", value: formatDocMoney(doc.totalVat, doc.currency) }
        : { label: null, value: "Без НДС" };
  return (
    <dl className="flex flex-col items-end gap-0.5 border-t border-[var(--border)] pt-3">
      <div className="flex items-baseline gap-3">
        <dt className="type-body text-on-surface-variant">Итого</dt>
        <dd className="type-section-title tabular-nums text-on-surface">{formatDocMoney(doc.totalGross, doc.currency)}</dd>
      </div>
      {vat ? (
        // relative: `sr-only` внутри позиционируется от строки, а не от страницы.
        <div className="relative flex items-baseline gap-3">
          <dt className={vat.label ? "type-caption text-on-surface-variant" : "sr-only"}>{vat.label ?? "НДС"}</dt>
          <dd className="type-caption tabular-nums text-on-surface-variant">{vat.value}</dd>
        </div>
      ) : null}
    </dl>
  );
}
